import { useState, useContext } from "react"
import cc from "classcat"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { useForm } from "react-hook-form"
import {
  Input,
  SubmitButton,
  PrivacyPolicyLink,
  Switch,
} from "components/atoms/form"
import { Modal } from "components/atoms/common/Modal"
import { getEuReportByTag } from "utils/forms"
import { siteCountry } from "utils/env"
import countries from "../../../../config/i18n"
import { EU_REPORT_2023_TAG } from "utils/constants"
import { Report2023Cover } from "./Report2023Cover"
import ContactInfoKaisa from "./ContactInfoKaisa"

const locales = countries[siteCountry] || countries.fi

const SubscribeForm = ({ setShowModal }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm()

  const {
    formFieldName,
    formFieldEmail,
    formErrorGeneral,
    formErrorUserExist,
    formErrorContactUs,
    subscribeHeaderSchool,
    reportDownloadButton,
    formFieldEntityTitle,
  } = useTranslations()

  const { locale } = useContext(LocaleContext)

  const [isSubmitError, setSubmitError] = useState(false)
  const [errMessage, setErrMessage] = useState(formErrorGeneral)

  const onSubmit = async (data) => {
    try {
      let res
      if (process.env.NODE_ENV === "development") {
        res = { status: 200 }
      } else {
        res = await getEuReportByTag(data, locale, EU_REPORT_2023_TAG)
      }

      if (res.status === 200) {
        setShowModal(true)
        reset()
        setSubmitError(false)
      }
    } catch ({ response }) {
      if (response?.data?.title === "Invalid Resource") {
        setErrMessage(response.data.detail)
      } else if (response?.data?.title === "Member Exists") {
        setErrMessage(formErrorUserExist)
      }
      setSubmitError(true)
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mb-0 space-y-4 rounded-lg bg-white px-4 pb-6 pt-6"
    >
      <Input
        label={formFieldName}
        name="name"
        control={control}
        required
        type="name"
        errors={errors}
      />
      <Input
        label={formFieldEmail}
        name="email"
        control={control}
        required
        type="email"
        errors={errors}
      />
      <Input label={formFieldEntityTitle} name="title" control={control} />
      <Switch
        label={subscribeHeaderSchool}
        name="somevahti"
        control={control}
      />
      <PrivacyPolicyLink text="" />
      <SubmitButton
        label={reportDownloadButton}
        isSubmitting={isSubmitting}
        isSubmitSuccessful={isSubmitSuccessful}
        isSubmitError={isSubmitError}
        errorMessage={`${errMessage} ${formErrorContactUs} ${locales[locale].contactEmail}`}
      />
    </form>
  )
}

export default function DownloadForm({ location = "blog" }) {
  const [showModal, setShowModal] = useState(false)
  const {
    webinarFormSuccessButton,
    report2023DownloadTitle,
    report2023DownloadDescription,
    formSuccess,
  } = useTranslations()
  return (
    <div
      className={cc([
        "relative bg-zinc-800",
        location === "page" ? "py-16 lg:py-24" : "rounded-lg py-8",
      ])}
      id="download-report"
    >
      <div className="mx-auto mb-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h3
            className={cc([
              "mb-0 font-display  font-extrabold  tracking-tight",
              location === "page"
                ? "text-4xl leading-normal sm:text-5xl"
                : "text-3xl leading-snug sm:text-4xl",
            ])}
          >
            <span className="whitespace-pre-line text-zinc-100">
              {report2023DownloadTitle}
            </span>
          </h3>
          <p className="prose mt-8 text-lg text-zinc-200 ">
            <span className="text-zinc-100">
              {report2023DownloadDescription}
            </span>
          </p>
        </div>
        <div className="grid-cols-6 gap-8 space-y-8 transition duration-150 ease-in-out md:grid md:gap-10 md:space-y-0">
          <div className="space-between col-span-2 flex flex-col">
            <Report2023Cover className="rounded-lg" />
            <ContactInfoKaisa />
          </div>
          <div className="col-span-4 mx-auto w-full">
            <div className="h-full lg:max-w-xl">
              <SubscribeForm setShowModal={setShowModal} />
            </div>
          </div>
        </div>
        <Modal
          isOpen={showModal}
          setIsOpen={setShowModal}
          title={formSuccess}
          bodyText=""
          buttonText={webinarFormSuccessButton}
        />
      </div>
    </div>
  )
}
